// entity-user.component.ts
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";

import { Technician, TechnicianFields } from "@app/@shared/models/technician";
import { Tenant, TenantFields } from "@app/@shared/models/tenant";
import { User, UserStatus, UserTypes } from "@app/@shared/models/users/user";

import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatSelectChange } from "@angular/material/select";
import { Logger } from "@app/@core";
import { AWSAuthService } from "@app/@core/services/aws/auth.service";
import { Company } from "@app/@shared/models/company";
import { ErrorNotificationService } from "@app/@shared/services/error_notification.service";
import { GeneralService } from "@app/@shared/services/general.service";
import { NotificationService } from "@app/@shared/services/notification.service";
import { UniqueUsernameValidator } from "@app/@shared/validators/unique-username-validator";
import { UsersService } from "@app/users/users.service";
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-entity-user",
  templateUrl: "./entity-user.component.html",
  styleUrls: ["./entity-user.component.scss"],
})
export class EntityUserComponent implements OnInit {
  log = new Logger(EntityUserComponent.name);

  private _entity: User;
  private _includeCompaniesSelector: Boolean = true;
  private _includeHousingSelector: Boolean = true;
  private _type: UserTypes = UserTypes.user;
  private _editable: Boolean | undefined;
  private _register: Boolean | undefined;
  private _includeAuthActions: Boolean = false;

  _hide: boolean = true;
  touch: boolean = false;
  userStatus = UserStatus;

  selectedCompany: Company; // kept for current entity visualization

  @Input() set entity(value: User) {
    this._entity = value;
    const uniqueUsernameValidator = new UniqueUsernameValidator(this.userService);
    this.userForm.controls.dni.addAsyncValidators(
      uniqueUsernameValidator.createValidator(this.entity?.dni),
    );

    if (value != null) {
      this.type = value.type;
      this.loadUserForm();
    }
  }
  get entity(): User {
    return this._entity;
  }

  @Input() set includeCompaniesSelector(value: Boolean) {
    this._includeCompaniesSelector = value;
  }
  get includeCompaniesSelector(): Boolean {
    return this._includeCompaniesSelector;
  }

  @Input() set includeHousingSelector(value: Boolean) {
    this._includeHousingSelector = value;
  }
  get includeHousingSelector(): Boolean {
    return this._includeHousingSelector;
  }

  @Input() set type(value: UserTypes) {
    this._type = value;

    // Remove dynamic controls before adding the ones for the selected type
    [
      "company_id",
      "email",
      "address",
      "workstation",
      "department",
      "contact",
      "code",
      "housing_ids",
    ].forEach((element) => {
      if (this.userForm.contains(element)) {
        this.userForm.removeControl(element);
      }
    });

    if (this.userForm) {
      this.userForm.controls.type.setValue(value, { emitEvent: false });
      let userType;

      if (value == UserTypes.technician) {
        const _entity = this.entity as Technician;

        this.userForm.addControl(
          "company_id",
          this.formBuilder.control(null, [Validators.required]),
        );
        this.selectedCompany = _entity?.company;

        this.userForm.addControl(
          "email",
          this.formBuilder.control(null, [Validators.required, Validators.email]),
        );
        this.userForm.addControl("address", this.formBuilder.control(_entity?.address));
        this.userForm.addControl("workstation", this.formBuilder.control(_entity?.workstation));
        this.userForm.addControl("department", this.formBuilder.control(_entity?.department));
        this.userForm.controls.role_id.setValue(4, { emitEvent: false });

      } else if (value == UserTypes.company_admin) {
        const _entity = this.entity as Technician;

        this.userForm.addControl(
          "company_id",
          this.formBuilder.control(null, [Validators.required]),
        );
        this.selectedCompany = _entity?.company;

        this.userForm.addControl(
          "email",
          this.formBuilder.control(null, [Validators.required, Validators.email]),
        );
        this.userForm.addControl("address", this.formBuilder.control(_entity?.address));
        this.userForm.addControl("workstation", this.formBuilder.control(_entity?.workstation));
        this.userForm.addControl("department", this.formBuilder.control(_entity?.department));
        this.userForm.controls.role_id.setValue(3, { emitEvent: false });

      } else if (value == UserTypes.tenant) {
        userType = TenantFields;
        const _entity: Tenant = this.entity as Tenant;

        this.userForm.addControl("email", this.formBuilder.control(null, [Validators.email]));
        this.userForm.addControl("contact", this.formBuilder.control(_entity?.contact));
        this.userForm.addControl("code", this.formBuilder.control(_entity?.code));

        const housing_ids: number[] = _entity?.housing?.map((h) => h.id);
        this.userForm.addControl("housing_ids", this.formBuilder.control(housing_ids));
        this.userForm.controls.role_id.setValue(2, { emitEvent: false });

      } else if (value == UserTypes.urvial_administrator) {
        this.userForm.addControl(
          "email",
          this.formBuilder.control(null, [Validators.required, Validators.email]),
        );
        this.userForm.controls.role_id.setValue(1, { emitEvent: false });
      }

      // If using TenantFields map, add required controls except id
      if (userType) {
        for (const field in userType) {
          if (field !== "id" && !this.userForm.contains(field)) {
            this.userForm.addControl(field, this.formBuilder.control("", [Validators.required]));
          }
        }
      }

      this.loadUserForm();
    }
  }
  get type(): UserTypes {
    return this._type;
  }

  @Input() set editable(value: Boolean | undefined) {
    this._editable = value;
    // Only enable/disable when explicitly set
    if (value === true) {
      this.userForm.enable({ emitEvent: false });
    } else if (value === false) {
      this.userForm.disable({ emitEvent: false });
    }
  }
  get editable(): Boolean | undefined {
    return this._editable;
  }

  @Input() set register(value: Boolean | undefined) {
    this._register = value;
    // Only enable/disable when explicitly set
    if (value === true) {
      this.userForm.enable({ emitEvent: false });
    } else if (value === false) {
      this.userForm.disable({ emitEvent: false });
    }
  }
  get register(): Boolean | undefined {
    return this._register;
  }

  @Input() set includeAuthActions(value: Boolean) {
    this._includeAuthActions = value;
  }
  get includeAuthActions(): Boolean {
    return this._includeAuthActions;
  }

  @Output() formEvent = new EventEmitter<FormGroup>();

  UserTypes = UserTypes;
  currentUserType: UserTypes = this.awsAuthService.currentUser?.type;
  userForm: FormGroup;

  isLoading: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UsersService,
    private awsAuthService: AWSAuthService,
    private notificationService: NotificationService,
    private errorNotificationService: ErrorNotificationService,
    public generalServices: GeneralService,
  ) {
    this.createUserForm();
  }

  ngOnInit() {
    // Emit initial form
    this.formEvent.emit(this.userForm);

    // Company admins: set a default type only when creating and no type was chosen
    if (
      this.currentUserType === UserTypes.company_admin &&
      !this.entity?.id &&
      !this.userForm.get("type")?.value
    ) {
      this.userForm.patchValue({ type: UserTypes.technician }, { emitEvent: false });
      this.onUserTypeChange({ value: UserTypes.technician } as any);
    }

    this.setupCompanyForCompanyAdmin();
  }

  createUserForm() {
    // Base form
    this.userForm = this.formBuilder.group({
      id: [null],
      type: [null, [Validators.required]],
      dni: ["", [Validators.required]],
      email: ["", [Validators.email, Validators.maxLength(80)]],
      username: ["", [Validators.maxLength(50)]],
      phone: ["",[Validators.required]],
      name: ["", [Validators.maxLength(50), Validators.required]],
      surname: ["", [Validators.maxLength(50), Validators.required]],
      role_id: [null],
    });

    // Creation mode: add password and enable fields
    if (!this.entity?.id) {
      this.userForm.addControl(
        "password",
        this.formBuilder.control(this.awsAuthService.generatePassword(), [Validators.required]),
      );
      this.userForm.controls.email.enable({ emitEvent: false });
      this.userForm.controls.username.enable({ emitEvent: false });
      this.userForm.controls.type.enable({ emitEvent: false });
      this.userForm.controls.name.enable({ emitEvent: false });
      this.userForm.controls.surname.enable({ emitEvent: false });
      this.userForm.controls.dni.enable({ emitEvent: false });
      this.userForm.controls.phone.enable({ emitEvent: false });
    }

    // Mirror DNI into username
    this.userForm.get("dni")?.valueChanges.subscribe((value) => {
      try {
        this.userForm.get("username")?.setValue(value, { emitEvent: false });
      } catch {
        this.userForm.get("username")?.reset("", { emitEvent: false });
      }
    });

    // Emit form changes
    this.userForm.valueChanges
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(() => this.formEvent.emit(this.userForm));
  }

  loadUserForm() {
    // Ensure password presence based on create/edit mode
    this.userForm.removeControl("password");
    const _entity = Object.assign({}, this.entity);

    if (_entity?.id) {
      delete (_entity as any).created_on;
      delete (_entity as any).updated_on;
    } else {
      this.userForm.addControl(
        "password",
        this.formBuilder.control(this.awsAuthService.generatePassword(), [Validators.required]),
      );
    }

    if (this.entity?.id) {
      const isAdmin = this.currentUserType === UserTypes.urvial_administrator;

      if (isAdmin) {
        // Admins: enable the whole form at group level
        this.userForm.enable({ emitEvent: false });
      } else {
        // Non-admin path: disable all, then selectively enable some keys
        const controlKeys = Object.keys(this.userForm.controls);
        const enableKeys = [
          "name",
          "surname",
          "phone",
          "contact",
          "code",
          "address",
          "workstation",
          "department",
          "type", // keep 'type' interactive for non-admins too (optional)
        ];

        for (const key of controlKeys) {
          this.userForm.controls[key].disable({ emitEvent: false });

          if (
            enableKeys.includes(key) &&
            this.currentUserType == UserTypes.company_admin &&
            _entity.status == UserStatus.confirmed
          ) {
            this.userForm.controls[key].enable({ emitEvent: false });
          }
          if (key === "email" && !_entity.email) {
            this.userForm.controls[key].enable({ emitEvent: false });
          }
          if (key === "contact" && this.currentUserType == UserTypes.urvial_administrator) {
            this.userForm.controls[key].enable({ emitEvent: false });
          }
        }
      }
    }

    // Patch entity values
    this.userForm.patchValue(_entity, { emitEvent: false });

    // Normalize phone to E.164 so intl-tel-input reads it and 'required' doesn't trigger
    const normalizedPhone = this.toE164FromBackend(_entity?.phone, "ES");
    if (normalizedPhone) {
      this.userForm.get("phone")?.setValue(normalizedPhone, { emitEvent: false });
    }

    // Always ensure 'type' stays enabled
    this.userForm.get("type")?.enable({ emitEvent: false });

    // Ensure company_id matches role after patching
    this.setupCompanyForCompanyAdmin();
  }

  onSelectCompany(control: FormControl) {
    this.userForm.controls.company_id.setValue(control.value.id, { emitEvent: false });
  }

  onAdminChange(event: MatCheckboxChange) {
    if (event.checked) {
      this.userForm.controls.role_id.setValue(3, { emitEvent: false });
    } else {
      this.userForm.controls.role_id.setValue(4, { emitEvent: false });
    }
  }

  onDisableClick() {
    this.updateUser({
      id: this.entity.id,
      type: this.type,
      status: UserStatus.disabled,
    } as User);
  }

  onEnableClick() {
    this.updateUser({
      id: this.entity.id,
      type: this.type,
      status: UserStatus.confirmed,
    } as User);
  }

  updateUser(user: User) {
    this.isLoading = true;

    this.userService
      .updateUser(user)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (u: User) => {
          this.log.info("User updated successful");
          this.notificationService.open("SuccessFullyUpdate");
          this.entity = u;
        },
        error: (error) => {
          this.log.error("Error updating User", error);
          this.errorNotificationService.notification(error, "ErrorUpdatingData");
        },
      });
  }

  onUserTypeChange(event: MatSelectChange) {
    this.type = event.value;
  }

  // All comments must be in English
  formatHousing = (item: any): string => {
    if (!item) return "";
    const abbr = (item.abbreviation ?? "").trim();
    const street = (item.full_street ?? item.street ?? "").trim();
    return abbr ? `[${abbr}] ${street}` : street;
  };

  // Converts backend phone string to E.164 expected by intl-tel-input (Spain default)
  private toE164FromBackend(raw: any, defaultCountry: "ES" | "es" = "ES"): string | null {
    if (!raw) return null;

    const trimmed = String(raw).trim();
    const digits = trimmed.replace(/\D+/g, "");

    // Already E.164
    if (trimmed.startsWith("+")) return trimmed;

    // Spain rules: national 9-digit -> +34 + 9; or 34+9 without plus -> +34...
    if (defaultCountry.toUpperCase() === "ES") {
      if (digits.length === 9) return `+34${digits}`;
      if (digits.length === 11 && digits.startsWith("34")) return `+${digits}`;
    }

    // Fallback
    return trimmed || null;
  }

  // Opción A: do not pass arrays/objects to [entity]; just preselect and lock the control for company_admin
  private setupCompanyForCompanyAdmin(): void {
    const companyCtrl = this.userForm.get("company_id");

    // Only act when the control exists (technician or company_admin types)
    if (!companyCtrl) return;

    if (this.currentUserType === UserTypes.company_admin) {
      const currentCompanyId = Number(this.awsAuthService.currentUser?.company_id);

      // Set current company as value if present
      if (currentCompanyId) {
        companyCtrl.setValue(currentCompanyId, { emitEvent: false });
      }

      // Lock control so company_admin cannot change it
      companyCtrl.disable({ emitEvent: false });
    } else {
      // Other roles can change the company
      companyCtrl.enable({ emitEvent: false });
    }
  }
}
